/**
 * Os contatos capturados — a resposta ao ponto do planejamento: "o nome / telefone precisa ir para
 * algum lugar".
 *
 * É a única tela do painel que mostra **dado pessoal**, e o desenho assume isso:
 *
 * - o interruptor de coleta fica em cima, visível, e não escondido em configurações avançadas;
 * - cada lead pode ser excluído individualmente (as Rules liberam `delete` ao dono justamente para
 *   atender pedido de exclusão);
 * - a exportação é CSV gerado no navegador — o dado não passa por nenhum servidor nosso a mais do
 *   que já passou.
 *
 * A lista virou **tabela** ao ganhar a tela larga. Na coluna de 384px cada lead era um card com uma
 * `<dl>` empilhada: para comparar o telefone de dois contatos era preciso rolar entre eles. Com uma
 * coluna por variável, a comparação é horizontal — que é o que se faz com uma lista de contatos.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEditor } from '../../store/editor.js';
import { getDataSource, isDemoMode, type LeadEntry } from '../../data/index.js';
import { Badge, Button, EmptyState, IconButton, Notice, Section, Switch } from '../ui.js';
import { IconeAtualizar, IconeBaixar, IconeLeads, IconeLixeira } from '../icons.js';

function formatarData(valor: number | null): string {
  if (!valor) return '—';
  return new Date(valor).toLocaleString('pt-BR');
}

/**
 * CSV com escape correto: valor com vírgula, aspas ou quebra de linha vai entre aspas, e aspas
 * internas são duplicadas. Sem isso, um nome com vírgula desloca todas as colunas seguintes.
 */
function paraCsv(leads: LeadEntry[]): string {
  const colunas = [...new Set(leads.flatMap((lead) => Object.keys(lead.variables)))].sort();
  const cabecalho = ['data', ...colunas, 'pagina'];

  const escapar = (valor: unknown): string => {
    const texto = valor === null || valor === undefined ? '' : String(valor);
    return /[",\n;]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
  };

  const linhas = leads.map((lead) =>
    [
      escapar(lead.createdAt ? new Date(lead.createdAt).toISOString() : ''),
      ...colunas.map((coluna) => escapar(lead.variables[coluna])),
      escapar(lead.pageUrl ?? ''),
    ].join(','),
  );

  return [cabecalho.join(','), ...linhas].join('\n');
}

function baixarCsv(nome: string, conteudo: string): void {
  // `﻿` (BOM) faz o Excel abrir o arquivo em UTF-8; sem ele, acento vira caractere estranho.
  const blob = new Blob([`﻿${conteudo}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nome;
  link.click();
  URL.revokeObjectURL(url);
}

const TH = 'px-4 py-2 text-xs font-semibold tracking-wide text-slate-500 uppercase whitespace-nowrap';

export function LeadsPanel() {
  const id = useEditor((state) => state.id);
  const nome = useEditor((state) => state.name);
  const collectLeads = useEditor((state) => state.collectLeads);
  const setCollectLeads = useEditor((state) => state.setCollectLeads);
  const [leads, setLeads] = useState<LeadEntry[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aberto, setAberto] = useState<string | null>(null);

  const carregar = useCallback(async (): Promise<void> => {
    if (!id) return;
    setCarregando(true);
    setErro(null);
    try {
      setLeads(await getDataSource().listLeads(id, 100));
    } catch (caught) {
      setErro(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setCarregando(false);
    }
  }, [id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const excluir = async (leadId: string): Promise<void> => {
    if (!id) return;
    if (!window.confirm('Excluir este lead? A ação não tem volta.')) return;
    await getDataSource().deleteLead(id, leadId);
    setLeads((atual) => atual.filter((lead) => lead.id !== leadId));
  };

  /*
   * As colunas são a união das variáveis de todos os leads, e não um conjunto fixo: o fluxo é do
   * cliente e pode perguntar qualquer coisa. Um lead antigo, capturado antes de um campo novo
   * existir, aparece com a célula vazia em vez de sumir da tabela.
   */
  const colunas = useMemo(
    () => [...new Set(leads.flatMap((lead) => Object.keys(lead.variables)))].sort(),
    [leads],
  );

  return (
    <div className="space-y-6">
      <Switch
        checked={collectLeads}
        tone="warning"
        label="Guardar os contatos capturados"
        description={
          <>
            Ao fim da conversa, as variáveis coletadas são gravadas neste webchat. É{' '}
            <strong>dado pessoal</strong> — nome, telefone, o que o fluxo perguntar. Desligado, o
            widget não envia e o servidor não guarda. Os números de conversa continuam sendo contados:
            lá não vai nada digitado pelo visitante.
          </>
        }
        onChange={setCollectLeads}
      />

      {isDemoMode ? (
        <Notice tone="warn" title="Sem contatos no modo demo">
          Os leads chegam do widget rodando num site de verdade. O preview do painel não envia nada.
        </Notice>
      ) : (
        <>
          {erro ? (
            <Notice tone="danger" title="Não foi possível carregar">
              {erro}
            </Notice>
          ) : null}

          <Section
            icon={IconeLeads}
            title={`${leads.length} ${leads.length === 1 ? 'contato' : 'contatos'}`}
            description="Os 100 mais recentes. A exportação gera o arquivo aqui no navegador."
            padded={false}
            action={
              <>
                <IconButton icon={IconeAtualizar} label="Atualizar contatos" onClick={() => void carregar()} />
                <Button
                  disabled={leads.length === 0}
                  onClick={() =>
                    baixarCsv(
                      `leads-${(nome || 'webchat').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`,
                      paraCsv(leads),
                    )
                  }
                >
                  <IconeBaixar />
                  Baixar CSV
                </Button>
              </>
            }
          >
            {carregando ? (
              <p className="p-4 text-sm text-slate-500">Carregando…</p>
            ) : leads.length === 0 ? (
              <EmptyState
                icon={IconeLeads}
                title="Nenhum contato ainda"
                description="Publique o webchat, instale a tag num site autorizado e conclua uma conversa. O contato aparece aqui assim que o fluxo termina."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className={TH}>Quando</th>
                      {colunas.map((coluna) => (
                        <th key={coluna} className={`${TH} font-mono normal-case`}>
                          {coluna}
                        </th>
                      ))}
                      <th className={TH}>Origem</th>
                      <th className={`${TH} text-right`}>
                        <span className="sr-only">Ações</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {leads.map((lead) => (
                      <tr key={lead.id} className="align-top">
                        <td className="px-4 py-3 text-sm whitespace-nowrap text-slate-500">
                          {formatarData(lead.createdAt)}
                        </td>
                        {colunas.map((coluna) => (
                          <td key={coluna} className="max-w-xs px-4 py-3 text-sm font-medium text-slate-800">
                            <span className="block truncate" title={String(lead.variables[coluna] ?? '')}>
                              {lead.variables[coluna] === undefined ? '—' : String(lead.variables[coluna])}
                            </span>
                          </td>
                        ))}
                        <td className="max-w-xs px-4 py-3">
                          <span className="block truncate text-sm text-slate-500" title={lead.pageUrl ?? ''}>
                            {lead.pageUrl ?? '—'}
                          </span>
                          <span className="mt-1 flex flex-wrap items-center gap-2">
                            {lead.sdkVersion ? <Badge>SDK {lead.sdkVersion}</Badge> : null}
                            {lead.transcript.length > 0 ? (
                              <button
                                type="button"
                                className="text-sm text-slate-600 underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
                                aria-expanded={aberto === lead.id}
                                onClick={() => setAberto(aberto === lead.id ? null : lead.id)}
                              >
                                {aberto === lead.id ? 'esconder conversa' : 'ver conversa'}
                              </button>
                            ) : null}
                          </span>

                          {aberto === lead.id ? (
                            <div className="mt-2 space-y-1 border-t border-slate-100 pt-2">
                              {lead.transcript.map((fala, indice) => (
                                <p key={indice} className="text-sm">
                                  <span
                                    className={`mr-2 inline-block rounded px-2 py-1 text-xs leading-none font-bold uppercase ${
                                      fala.role === 'visitor'
                                        ? 'bg-sky-100 text-sky-800'
                                        : fala.role === 'system'
                                          ? 'bg-amber-100 text-amber-900'
                                          : 'bg-slate-100 text-slate-700'
                                    }`}
                                  >
                                    {fala.role === 'visitor'
                                      ? 'visitante'
                                      : fala.role === 'system'
                                        ? 'sistema'
                                        : 'bot'}
                                  </span>
                                  <span className="text-slate-700">{fala.text}</span>
                                </p>
                              ))}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <IconButton
                            icon={IconeLixeira}
                            label={`Excluir o contato de ${formatarData(lead.createdAt)}`}
                            variant="danger"
                            onClick={() => void excluir(lead.id)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </>
      )}
    </div>
  );
}
